"use client";

import { Check, X } from "lucide-react";
import type * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/atoms/button";
import {
  DS_SPARKLE_DURATION_MS,
  SparkleBurst,
} from "@/components/ui/atoms/sparkle-burst";
import {
  StepIndicator,
  stepIndicatorIconVariants,
  stepIndicatorLabelVariants,
} from "@/components/ui/atoms/step-indicator";
import { cn } from "@/lib/utils";

export type StepStatus = "incomplete" | "active" | "complete";

export type Step = {
  id: string;
  label: string;
  status: StepStatus;
  onClick?: () => void;
};

export type StepsProgressProps = {
  steps: Step[];
  onDismiss?: () => void;
  /**
   * When true, `StepsProgress` watches for any step whose status transitions
   * into `complete` and renders a one-shot `SparkleBurst` overlay on that step
   * to celebrate. The overlay is `aria-hidden` + `pointer-events-none`, so the
   * underlying step (including its click handler) remains fully interactive.
   *
   * Nothing animates on initial mount — only on subsequent status changes —
   * so already-complete steps won't sparkle on first render. The sparkle lasts
   * for `DS_SPARKLE_DURATION_MS` (see `sparkle-burst.tsx`) and then clears.
   *
   * Respects `prefers-reduced-motion` via token CSS.
   */
  animateStepTransitions?: boolean;
  /** Render steps in a vertical list instead of a horizontal bar. */
  orientation?: "horizontal" | "vertical";
} & React.ComponentProps<"section">;

type StepContentProps = {
  step: Step;
  stepNumber: number;
  isAttention?: boolean;
};

function StepContent({ step, stepNumber, isAttention }: StepContentProps) {
  const indicator = (
    <StepIndicator
      className="min-w-0"
      label={step.label}
      status={step.status}
      stepNumber={stepNumber}
    />
  );

  const targetClass = cn(
    "flex min-w-0 flex-1 items-center justify-center",
    isAttention && "step-attention-target"
  );

  if (step.onClick) {
    return (
      <button
        className={cn(targetClass, "cursor-pointer")}
        onClick={step.onClick}
        type="button"
      >
        {indicator}
      </button>
    );
  }

  return <span className={targetClass}>{indicator}</span>;
}

function getLastStepIndex(steps: Step[]) {
  return steps.length - 1;
}

function getActiveStepIndex(steps: Step[]) {
  return steps.findIndex((step) => step.status === "active");
}

function getMaxMobileIndex(steps: Step[], isDismissible: boolean) {
  if (steps.length === 0) {
    return isDismissible ? 0 : -1;
  }

  return isDismissible ? steps.length : getLastStepIndex(steps);
}

export function getMobileIndexForSteps(
  steps: Step[],
  options?: {
    currentIndex?: number;
    isDismissible?: boolean;
  }
) {
  const { currentIndex, isDismissible = false } = options ?? {};
  const activeIndex = getActiveStepIndex(steps);

  if (activeIndex >= 0) {
    return activeIndex;
  }

  if (steps.length === 0) {
    return isDismissible ? 0 : -1;
  }

  const maxIndex = getMaxMobileIndex(steps, isDismissible);

  if (
    currentIndex !== undefined &&
    currentIndex >= 0 &&
    currentIndex <= maxIndex
  ) {
    return currentIndex;
  }

  return getLastStepIndex(steps);
}

function getDesktopTrailingLineLeft(stepsLength: number) {
  return `calc(${(stepsLength - 1) / stepsLength} * (100% - 4.25rem) + 0.75rem)`;
}

export function StepsProgress({
  steps,
  onDismiss,
  animateStepTransitions = false,
  orientation = "horizontal",
  className,
  ...props
}: StepsProgressProps) {
  const isDismissible = Boolean(onDismiss);

  // Track which step (if any) just transitioned into `complete` so we can
  // render a one-shot sparkle overlay on it. `key` bumps each time the sparkle
  // retriggers so the underlying `SparkleBurst` fully remounts and replays.
  const [sparkle, setSparkle] = useState<
    { stepId: string; key: number } | undefined
  >(undefined);
  const prevStatusesRef = useRef<Map<string, StepStatus> | null>(null);
  const sparkleTimerRef = useRef<number | null>(null);
  const sparkleKeyRef = useRef(0);

  useEffect(() => {
    const prev = prevStatusesRef.current;
    const next = new Map(steps.map((s) => [s.id, s.status]));
    // Seed on first render so initial `complete` steps don't sparkle.
    if (prev === null) {
      prevStatusesRef.current = next;
      return;
    }

    if (animateStepTransitions) {
      const justCompleted = steps.find(
        (s) => s.status === "complete" && prev.get(s.id) !== "complete"
      );
      if (justCompleted) {
        if (sparkleTimerRef.current !== null) {
          window.clearTimeout(sparkleTimerRef.current);
        }
        sparkleKeyRef.current += 1;
        setSparkle({ stepId: justCompleted.id, key: sparkleKeyRef.current });
        sparkleTimerRef.current = window.setTimeout(() => {
          setSparkle(undefined);
          sparkleTimerRef.current = null;
        }, DS_SPARKLE_DURATION_MS);
      }
    }

    prevStatusesRef.current = next;
  }, [animateStepTransitions, steps]);

  useEffect(
    () => () => {
      if (sparkleTimerRef.current !== null) {
        window.clearTimeout(sparkleTimerRef.current);
      }
    },
    []
  );

  const visibleMobileIndex = getMobileIndexForSteps(steps);
  const mobileStep = steps[visibleMobileIndex];
  const showDesktopTrailingLine =
    isDismissible && steps.at(-1)?.status === "active";

  if (orientation === "vertical") {
    return (
      <section aria-label="Steps progress" className={cn(className)} {...props}>
        <ol className="flex flex-col">
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            return (
              <li
                className={cn("flex items-start gap-3", !isLast && "pb-5")}
                key={step.id}
              >
                <div className="flex shrink-0 flex-col items-center self-stretch">
                  <span
                    className={cn(
                      stepIndicatorIconVariants({ status: step.status })
                    )}
                  >
                    {step.status === "complete" ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <span className="text-sm">{index + 1}</span>
                    )}
                  </span>
                  {!isLast && <div className="mt-2 w-px flex-1 bg-border" />}
                </div>
                <span
                  aria-current={step.status === "active" ? "step" : undefined}
                  className={cn(
                    stepIndicatorLabelVariants({ status: step.status }),
                    "pt-0.5"
                  )}
                >
                  {step.label}
                  <span className="sr-only"> — {step.status}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </section>
    );
  }

  return (
    <section
      aria-label="Steps progress"
      className={cn(
        "relative h-14 overflow-hidden border border-border bg-card text-foreground",
        className
      )}
      {...props}
    >
      {showDesktopTrailingLine && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-0 right-0 z-10 hidden h-0.5 bg-foreground sm:block"
          style={{ left: getDesktopTrailingLineLeft(steps.length) }}
        />
      )}
      {mobileStep?.status === "active" && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 z-20 h-0.5 bg-foreground sm:hidden"
        />
      )}

      <div className="relative z-10 flex h-full items-center sm:gap-3 sm:px-3">
        <ol className="flex h-full min-w-0 flex-1 items-center gap-0">
          {steps.map((step, index) => {
            const isAttention = sparkle?.stepId === step.id;
            return (
              <li
                aria-current={step.status === "active" ? "step" : undefined}
                className={cn(
                  "relative isolate flex h-full min-w-0 flex-1 items-center justify-center px-2 py-4 sm:px-3",
                  index !== visibleMobileIndex && "hidden sm:flex"
                )}
                data-attention={isAttention ? "true" : undefined}
                data-status={step.status}
                key={step.id}
              >
                {index > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 hidden h-full w-px bg-border sm:block"
                  />
                )}
                <StepContent
                  isAttention={isAttention}
                  step={step}
                  stepNumber={index + 1}
                />
                {isAttention ? (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center"
                  >
                    <SparkleBurst key={sparkle.key} />
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>

        {steps.length > 0 && mobileStep && (
          <span
            aria-hidden="true"
            className="shrink-0 px-3 font-mono text-muted-foreground text-xs tabular-nums sm:hidden"
          >
            {visibleMobileIndex + 1} / {steps.length}
          </span>
        )}

        {onDismiss && (
          <>
            <Button
              aria-label="Dismiss"
              className="h-8 shrink-0 gap-1.5 border border-border px-3 text-muted-foreground hover:bg-muted hover:text-foreground sm:hidden"
              onClick={onDismiss}
              type="button"
              variant="ghost"
            >
              <X className="h-4 w-4" />
              <span className="text-sm">Dismiss</span>
            </Button>
            <Button
              aria-label="Dismiss steps progress"
              className="hidden h-8 w-8 shrink-0 border border-border text-muted-foreground hover:bg-muted hover:text-foreground sm:flex"
              onClick={onDismiss}
              size="icon"
              type="button"
              variant="ghost"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </section>
  );
}
